import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import LoginPage from './components/LoginPage';
import AdminAccessDeniedPage from './pages/AdminAccessDeniedPage';
const Home = lazy(() => import('./pages/Home'));
const PipelinesPage = lazy(() => import('./pages/PipelinesPage'));
const SoftwareInstallationPage = lazy(() => import('./pages/SoftwareInstallationPage'));
const HardwareManagementPage = lazy(() => import('./pages/HardwareManagementPage'));
const AdminPanelPage = lazy(() => import('./pages/AdminPanelPage'));
const InfrastructurePortalPage = lazy(() => import('./pages/InfrastructurePortalPage'));
const ExecutionRequestPage = lazy(() => import('./pages/ExecutionRequestPage'));
const ApprovalsQueuePage = lazy(() => import('./pages/ApprovalsQueuePage'));
const ApprovalDetailPage = lazy(() => import('./pages/ApprovalDetailPage'));
const RequestDashboardPage = lazy(() => import('./pages/RequestDashboardPage'));
const ArgusPcnControlTowerPage = lazy(() => import('./pages/ArgusPcnControlTowerPage'));
const ActionCatalogPage = lazy(() => import('./pages/ActionCatalogPage'));
const PlaybooksPage = lazy(() => import('./pages/PlaybooksPage'));
const CatalogPlanApprovalHubPage = lazy(() => import('./pages/CatalogPlanApprovalHubPage'));
const ProposeActionPage = lazy(() => import('./pages/ProposeActionPage'));
const ProposalDetailPage = lazy(() => import('./pages/ProposalDetailPage'));
const RefineriesWorldMapPage = lazy(() => import('./pages/RefineriesWorldMapPage'));
const MachineEnrollmentPage = lazy(() => import('./pages/MachineEnrollmentPage'));

function ProtectedAdminPanelRoute() {
  const { isLoading, isAdmin } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-white to-[#E6F0F9] flex items-center justify-center">
        <div className="text-sm text-gray-500">Checking authorization...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return <AdminAccessDeniedPage />;
  }

  return <AdminPanelPage />;
}

function AppRouter() {
  return (
    <>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <Suspense
          fallback={
            <div className="min-h-screen bg-gradient-to-br from-[#F8F9FA] via-white to-[#E6F0F9] flex items-center justify-center">
              <div className="text-sm text-gray-500">Loading page...</div>
            </div>
          }
        >
          <Routes>
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="/home" element={<Home />} />
            <Route path="/running-plans" element={<PipelinesPage />} />
            <Route path="/software-installation" element={<SoftwareInstallationPage />} />
            <Route path="/hardware-management" element={<HardwareManagementPage />} />
            <Route path="/admin-panel" element={<ProtectedAdminPanelRoute />} />
            <Route path="/infrastructure-portal" element={<InfrastructurePortalPage />} />
            <Route path="/build-new-plan" element={<ExecutionRequestPage />} />
            <Route path="/execution-request" element={<ExecutionRequestPage />} />
            <Route path="/pcn-control-tower" element={<ArgusPcnControlTowerPage />} />
            <Route path="/action-catalog" element={<ActionCatalogPage />} />
            <Route path="/playbooks" element={<PlaybooksPage />} />
            <Route path="/approvals/hub" element={<CatalogPlanApprovalHubPage />} />
            <Route path="/refineries-map" element={<RefineriesWorldMapPage />} />
            <Route path="/action-catalog/propose" element={<ProposeActionPage />} />
            <Route path="/action-catalog/proposal/:proposalId" element={<ProposalDetailPage />} />
            <Route path="/approvals/queue" element={<ApprovalsQueuePage />} />
            <Route path="/approvals/dashboard" element={<RequestDashboardPage />} />
            <Route path="/approvals/detail/:requestId" element={<ApprovalDetailPage />} />
            <Route path="/machine-enrollment" element={<MachineEnrollmentPage />} />
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
        </Suspense>
      </AuthenticatedTemplate>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ThemeProvider>
          <AppRouter />
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
